import {
  ArrowBackIosNew,
  Check,
  Visibility,
  VisibilityOff,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { logoutUser } from "../../actions";
import CheckText from "../../components/CheckText";
import LoadingButton from "../../components/LoadingButton";
import { useResetUserPasswordMutation } from "../../services/portal/portalApi";

type ResetPasswordProps = {
  onBack: () => void;
  token: string;
  email: string;
};

const ResetPassword = ({ onBack, token, email }: ResetPasswordProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const dispatch = useDispatch();

  const [
    resetPassword,
    { isLoading, error, isSuccess },
  ] = useResetUserPasswordMutation();
  const formRef = useRef<any>(null);
  useEffect(() => {
    //kill session if there is any
    dispatch(logoutUser());
  }, [dispatch]);

  const handleSendResetLink = async (values: any) => {
    try {
      await resetPassword({
        email,
        password: values.password,
        password_confirmation: values.confirmation,
        token,
      }).unwrap();
    } catch (e) {
      console.log(e);
      enqueueSnackbar("Error while resetting password", { variant: "error" });
    }
  };

  return (
    <Box
      sx={{
        "input::-ms-reveal,input::-ms-clear": {
          display: "none",
        },
      }}
    >
      <Formik
        ref={formRef}
        initialValues={{ password: "", confirmation: "" }}
        onSubmit={handleSendResetLink}
      >
        {({ isSubmitting, values }) => {
          const { password, confirmation } = values;
          const char8 = password.length >= 8;
          const oneUppercase = /[A-Z]/.test(password);
          const oneLowercase = /[a-z]/.test(password);
          const oneNumber = /[0-9]/.test(password);
          const oneSpecial = /[^A-Za-z0-9]/.test(password);
          const passwordValid =
            char8 && oneUppercase && oneLowercase && oneNumber && oneSpecial;
          const match = confirmation && password === confirmation;
          return (
            <Form autoCapitalize="off">
              <Typography
                variant="h1"
                sx={{ fontSize: 32, fontWeight: 400, mb: "8px" }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body"
                sx={{
                  mb: "24px",
                  display: "block",
                  lineHeight: "21px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "333px",
                }}
                color="text.disabled"
              >
                {email}
              </Typography>
              <Field
                component={TextField}
                label="New password"
                name="password"
                autoFocus
                type={showPassword ? "text" : "password"}
                fullWidth
                variant="filled"
                disabled={isSuccess}
                sx={{ mb: "16px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className="end"
                        sx={{
                          opacity: password ? 1 : 0,
                          color: "text.tertiary",
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: "0px 5px",
                  flexWrap: "wrap",
                  marginTop: "-12px",
                  marginBottom: "16px",
                }}
              >
                {[
                  { text: "8 characters", checked: char8 },
                  { text: "1 uppercase", checked: oneUppercase },
                  { text: "1 lowercase", checked: oneLowercase },
                  { text: "1 number", checked: oneNumber },
                  { text: "1 special character", checked: oneSpecial },
                ].map((item, index) => (
                  <CheckText key={index} checked={item.checked}>
                    {item.text}
                  </CheckText>
                ))}
              </Box>
              <Field
                component={TextField}
                label="Confirm password"
                name="confirmation"
                variant="filled"
                disabled={isSuccess}
                type={showConfirmation ? "text" : "password"}
                fullWidth
                sx={{ mb: "16px" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className="end"
                        sx={{
                          opacity: confirmation ? 1 : 0,
                          color: "text.tertiary",
                        }}
                        onClick={() => setShowConfirmation(!showConfirmation)}
                      >
                        {showConfirmation ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ marginTop: "-12px", marginBottom: "16px" }}>
                <CheckText checked={!!match}>Match</CheckText>
              </Box>

              {!isSuccess && (
                <LoadingButton
                  size="large"
                  loading={isLoading}
                  variant="contained"
                  fullWidth
                  disabled={!(passwordValid && match)}
                  type="submit"
                  sx={{ mb: "8px" }}
                >
                  Change Password
                </LoadingButton>
              )}
              {isSuccess && (
                <Alert
                  severity="success"
                  sx={{ flexWrap: "wrap" }}
                  variant="outlined"
                  icon={<Check />}
                >
                  Password updated!
                </Alert>
              )}
              <Box
                color="inherit"
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "text.secondary",
                  mt: "24px",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onClick={onBack}
              >
                <ArrowBackIosNew sx={{ fontSize: 16 }} /> Back to login
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default ResetPassword;
